/**
 * Google Analytics — on by default.
 *
 * No consent banner: product decision made explicitly, with the tradeoff
 * understood (see the commit that removed `CookieConsent.jsx` and
 * `useConsent`/`setConsent`/the whole opt-in flow this file used to gate
 * behind). Analytics loads for every visitor automatically. The Privacy
 * Policy (`server/src/data/legal.js`, section `cookies`) is what has to stay
 * honest about that — this file is what has to make it true.
 *
 * The one thing still honoured automatically is a Global Privacy Control
 * signal — a distinct, standing site promise ("we honour it as an opt-out
 * request") that predates and isn't the same mechanism as the removed
 * banner, so it stayed.
 */

/** From the Google Analytics 4 data stream's own install snippet. Not a
 * secret — a Measurement ID identifies a property, it does not authorise
 * writing to it. */
export const GA_MEASUREMENT_ID = 'G-F1Q10KYPT4';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
  interface Navigator {
    /** Set by a Global Privacy Control–signalling browser or extension.
     * Not on every navigator, hence optional rather than `boolean`. */
    globalPrivacyControl?: boolean;
  }
}

export function hasGPCSignal(): boolean {
  return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
}

let gaLoaded = false;

/** Injects the Google tag exactly once. Safe to call more than once — every
 * caller after the first is a no-op. */
function loadGoogleAnalytics(): void {
  if (gaLoaded || typeof document === 'undefined') return;
  gaLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag.js only processes dataLayer entries that are the `arguments` object
  // of a gtag() call — it uses arguments-ness to tell a gtag command apart
  // from plain data someone pushed onto the dataLayer. Pushing a normal array
  // (e.g. a `...args` rest parameter) instead means every `config` and
  // `event` command is silently ignored: the tag loads, initialises, and
  // never sends a single hit to Analytics. So this has to push `arguments`,
  // exactly like Google's canonical snippet.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // `send_page_view: false` because AnalyticsPageview (App.jsx) sends a
  // pageview itself on mount and on every route change. Without this, the
  // config call fires its own automatic pageview for the initial route on
  // top of that one — every session opened its landing page with two
  // pageviews, inflating pageview and entrance counts.
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

/** Call once, high in the tree (see `App.jsx`). Loads Analytics for every
 * visitor except one sending a Global Privacy Control signal. */
export function initAnalytics(): void {
  if (hasGPCSignal()) return;
  loadGoogleAnalytics();
}

/** Records a virtual pageview for the SPA route the caller just navigated
 * to. `gtag('config', ...)` above only fires the *first* pageview — a
 * client-routed app that never reloads has to send the rest itself, or
 * every page after the first is invisible to Analytics. No-ops before
 * Analytics has loaded (i.e. for a GPC-signalling browser). */
export function trackPageview(path: string): void {
  if (!gaLoaded || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
  });
}

/**
 * Records a custom interaction event — a CTA click, a filter toggle, a step
 * in the offering funnel. `params` must be structural metadata only (ids,
 * category names, booleans, amounts) — never a value someone typed into a
 * text field. The dedication, a donor's name, and an email address are
 * treated as sensitive data by the Privacy Policy (`server/src/data/
 * legal.js`, `collected` section); Google Analytics is a third party, so
 * putting free text in an event breaks that promise the same way leaking it
 * into a URL would. No-ops before Analytics has loaded, same as
 * `trackPageview`.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!gaLoaded || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}
